function rango(min, max) {
    return min + Math.random() * (max - min);
}

function ruido(amp) {
    return (Math.random() - 0.5) * 2 * amp;
}

function rellenarReposoBilateral(muestras, masaTotal, splitIzq, dt, tFinAbs) {
    if (!muestras.length) return muestras;
    let last = muestras[muestras.length - 1];
    while (last[2] < tFinAbs - dt * 0.2) {
        const nt = last[2] + dt;
        const [izq, der] = splitIzq(masaTotal + ruido(0.2));
        muestras.push([izq, der, nt]);
        last = muestras[muestras.length - 1];
    }
    return muestras;
}

/**
 * Formato igual al firmware Jumptech (BLE): tres valores por muestra.
 * String notificado: `${masaIzqKg},${masaDerKg},${tiempoSeg}` (ver 4.BLE_notif_Jumptech_sync.ino).
 * El tiempo debe ser monótono creciente (p. ej. millis/1000 del dispositivo).
 * @param {{ duracionSegundos?: number }} [opts] - si se pasa > duración del núcleo (~4s), se rellena en reposo hasta ese total (máx. 120s).
 */
export function generarMuestrasCmj(opts = {}) {
    const masaTotal = rango(72, 88);
    const parteIzq = rango(0.47, 0.53);
    const splitIzq = (comb) => {
        const j = comb * parteIzq + ruido(0.15);
        const d = comb - j;
        return [Math.max(0, j), Math.max(0, d)];
    };

    const dt = 0.0125;
    const n = 320;
    const t0 = performance.now() / 1000;
    const muestras = [];
    const durObjetivo = Math.min(120, Math.max(4, opts.duracionSegundos ?? 4));

    for (let i = 0; i < n; i++) {
        const t = i / n;
        let comb;

        if (t < 0.18) {
            comb = masaTotal + ruido(0.25);
        } else if (t < 0.28) {
            const u = (t - 0.18) / 0.1;
            comb = masaTotal * (1 - 0.42 * u) + ruido(0.2);
        } else if (t < 0.4) {
            const u = (t - 0.28) / 0.12;
            comb = masaTotal * (0.58 + 0.55 * u) + ruido(0.35);
        } else if (t < 0.48) {
            const u = (t - 0.4) / 0.08;
            comb = masaTotal * (1.13 * (1 - u) + 0.04 * u) + ruido(0.2);
        } else if (t < 0.56) {
            comb = rango(0.8, 2.2) + ruido(0.15);
        } else if (t < 0.68) {
            const u = (t - 0.56) / 0.12;
            comb = (rango(0.8, 2.2) * (1 - u) + masaTotal * 1.85 * u) + ruido(0.5);
        } else {
            const u = Math.min(1, (t - 0.68) / 0.32);
            comb = masaTotal * (1.85 * (1 - u) + u) + ruido(0.2 * (1 - u));
        }

        const [izq, der] = splitIzq(comb);
        const tAbs = t0 + i * dt;
        muestras.push([izq, der, tAbs]);
    }

    return rellenarReposoBilateral(muestras, masaTotal, splitIzq, dt, t0 + durObjetivo);
}

/** Nordic: mismas columnas BLE; curva sin vuelo, picos asimétricos por pierna. */
export function generarMuestrasNordic(opts = {}) {
    const dt = 0.0125;
    const n = 280;
    const t0 = performance.now() / 1000;
    const durObjetivo = Math.min(120, Math.max(4, opts.duracionSegundos ?? 4));
    const picoIzqKg = rango(48, 72);
    const picoDerKg = rango(52, 78);
    const baseIzq = rango(22, 34);
    const baseDer = rango(24, 36);

    const muestras = [];
    for (let i = 0; i < n; i++) {
        const t = i / n;
        let izq;
        let der;
        if (t < 0.12) {
            izq = baseIzq + ruido(0.05);
            der = baseDer + ruido(0.05);
        } else if (t < 0.45) {
            const u = (t - 0.12) / 0.33;
            const s = Math.sin(u * Math.PI * 0.5);
            izq = baseIzq + (picoIzqKg - baseIzq) * s + ruido(0.08);
            der = baseDer + (picoDerKg - baseDer) * (s * 0.92) + ruido(0.08);
        } else {
            const u = Math.min(1, (t - 0.45) / 0.55);
            izq = picoIzqKg * (1 - u) + baseIzq * u + ruido(0.06);
            der = picoDerKg * (1 - u) + baseDer * u + ruido(0.06);
        }
        muestras.push([Math.max(0, izq), Math.max(0, der), t0 + i * dt]);
    }
    const masaMedia = (baseIzq + baseDer) / 2;
    const splitN = (comb) => {
        const j = comb * 0.5 + ruido(0.08);
        return [Math.max(0, j), Math.max(0, comb - j)];
    };
    return rellenarReposoBilateral(muestras, masaMedia, splitN, dt, t0 + durObjetivo);
}

function rellenarReposoDynamo(muestras, reposo, dt, tFinAbs) {
    if (!muestras.length) return muestras;
    let last = muestras[muestras.length - 1];
    while (last[1] < tFinAbs - dt * 0.2) {
        const nt = last[1] + dt;
        muestras.push([Math.max(0, reposo + ruido(0.35)), nt]);
        last = muestras[muestras.length - 1];
    }
    return muestras;
}

/** Dynamo: el frontend espera [masaCombinadaKg, tiempoSeg] por evento. */
export function generarMuestrasDynamo(opts = {}) {
    const reposo = rango(18, 45);
    const pico = reposo + rango(25, 70);
    const dt = 0.015;
    const n = 200;
    const t0 = performance.now() / 1000;
    const durObjetivo = Math.min(120, Math.max(3, opts.duracionSegundos ?? 3));
    const muestras = [];
    for (let i = 0; i < n; i++) {
        const t = i / n;
        let m;
        if (t < 0.15) m = reposo + ruido(0.4);
        else if (t < 0.55) {
            const u = (t - 0.15) / 0.4;
            m = reposo + (pico - reposo) * Math.sin((u * Math.PI) / 2) + ruido(0.5);
        } else {
            const u = (t - 0.55) / 0.45;
            m = pico * (1 - u) + reposo * u + ruido(0.3);
        }
        muestras.push([Math.max(0, m), t0 + i * dt]);
    }
    return rellenarReposoDynamo(muestras, reposo, dt, t0 + durObjetivo);
}

// Geometría idéntica a Centro_de_presion.py (mm).
const COP_ANCHO_MM = 300;
const COP_LARGO_MM = 480;
// Mapeo de canales idéntico al Python:
// P1_AI=3, P1_AD=1, P1_PI=2, P1_PD=0, P2_AI=4, P2_AD=5, P2_PI=7, P2_PD=6.
const COP_INDICES_P1 = [3, 1, 2, 0];
const COP_INDICES_P2 = [4, 5, 7, 6];

/**
 * Port de fuerzas_desde_cop() del Python: reparte una fuerza total en las
 * cuatro esquinas [AI, AD, PI, PD] para un COP objetivo dentro de la plataforma.
 */
function fuerzasDesdeCop(copX, copY, fuerzaTotal, anchoMm, largoMm) {
    const propIzq = (anchoMm / 2 - copX) / anchoMm;
    const propDer = 1 - propIzq;
    const propAnt = (largoMm / 2 + copY) / largoMm;
    const propPost = 1 - propAnt;
    return [
        fuerzaTotal * propIzq * propAnt,
        fuerzaTotal * propDer * propAnt,
        fuerzaTotal * propIzq * propPost,
        fuerzaTotal * propDer * propPost,
    ];
}

/**
 * Port de ejecutar_demo() de Centro_de_presion.py.
 *
 * Cada muestra replica la trama serial del firmware:
 * [P1_S1..P1_S4, P2_S1..P2_S4, TIEMPO_MS] con fuerzas en N.
 * El COP y las fuerzas siguen las mismas sinusoides del demo:
 * COP P1 = [45·sin(2π·0.22·t), 70·sin(2π·0.16·t + 0.4)]
 * COP P2 = [35·sin(2π·0.19·t + 1.0), 60·sin(2π·0.14·t)]
 * Fz P1 = 360 + 35·sin(2π·0.11·t), Fz P2 = 310 − 35·sin(2π·0.11·t).
 *
 * El bloque inicial emite plataformas descargadas (ruido ≈ 0) para que la
 * tara del frontend calcule offsets/umbrales, igual que el Python pide
 * retirar toda carga antes de tarar.
 */
export function generarMuestrasCop(opts = {}) {
    const duracionSegundos = Math.min(120, Math.max(10, opts.duracionSegundos ?? 60));
    const taraSegundos = Math.max(0, opts.taraSegundos ?? 5);
    const dt = 0.012;
    const t0ms = performance.now();
    const muestras = [];
    let k = 0;

    const nTara = Math.ceil((taraSegundos + 0.5) / dt);
    for (let i = 0; i < nTara; i++) {
        const canales = Array.from({ length: 8 }, () => ruido(0.05));
        muestras.push([...canales, t0ms + k * dt * 1000]);
        k += 1;
    }

    const n = Math.ceil(duracionSegundos / dt);
    for (let i = 0; i < n; i++) {
        const t = i * dt;
        const copP1x = 45 * Math.sin(2 * Math.PI * 0.22 * t);
        const copP1y = 70 * Math.sin(2 * Math.PI * 0.16 * t + 0.4);
        const copP2x = 35 * Math.sin(2 * Math.PI * 0.19 * t + 1.0);
        const copP2y = 60 * Math.sin(2 * Math.PI * 0.14 * t);
        const fzP1 = 360 + 35 * Math.sin(2 * Math.PI * 0.11 * t);
        const fzP2 = 310 - 35 * Math.sin(2 * Math.PI * 0.11 * t);
        const fuerzasP1 = fuerzasDesdeCop(copP1x, copP1y, fzP1, COP_ANCHO_MM, COP_LARGO_MM);
        const fuerzasP2 = fuerzasDesdeCop(copP2x, copP2y, fzP2, COP_ANCHO_MM, COP_LARGO_MM);
        const canales = new Array(8).fill(0);
        fuerzasP1.forEach((f, j) => {
            canales[COP_INDICES_P1[j]] = Math.max(0, f + ruido(0.25));
        });
        fuerzasP2.forEach((f, j) => {
            canales[COP_INDICES_P2[j]] = Math.max(0, f + ruido(0.25));
        });
        muestras.push([...canales, t0ms + k * dt * 1000]);
        k += 1;
    }
    return muestras;
}
